<template>
  <!-- Reusable chart-options menu. The button stays inline next to a
       chart title; the dropdown itself teleports to <body> so it
       escapes any clipping context (parent panels with overflow:hidden,
       table wrappers with overflow-x-auto, etc.). -->
  <span class="relative inline-block" @click.stop>
    <button
      ref="buttonEl"
      type="button"
      @click="toggle"
      class="p-1.5 rounded-lg border border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
      title="Chart options"
      :aria-expanded="open"
    >
      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    </button>

    <!-- Teleported dropdown — fixed-positioned, anchored to the
         button's viewport rect. The full-screen overlay catches
         outside-click to close. -->
    <Teleport to="body">
      <template v-if="open">
        <div class="fixed inset-0 z-40" @click="open = false"></div>
        <div
          class="fixed z-50 w-64 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-800 shadow-xl py-2 text-sm"
          :style="menuStyle"
          @click.stop
        >
          <!-- ───────── Chart type ───────── -->
          <p class="px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Chart type</p>
          <button
            v-for="t in chartTypes"
            :key="t.value"
            type="button"
            @click="setType(t.value)"
            class="w-full px-3 py-1.5 flex items-center gap-2 text-left hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
            :class="modelValue === t.value ? 'text-purple-600 dark:text-purple-300 font-semibold' : 'text-gray-700 dark:text-gray-200'"
          >
            <svg v-if="t.value === 'curve'"  class="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 17c4-2 4-10 9-10s5 8 9 6" /></svg>
            <svg v-else-if="t.value === 'line'" class="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 17l6-6 4 4 8-8" /></svg>
            <svg v-else class="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 20V10m6 10V4m6 16v-7m6 7V8" /></svg>
            <span>{{ t.label }}</span>
            <svg v-if="modelValue === t.value" class="w-4 h-4 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" /></svg>
          </button>

          <!-- ───────── Bar layout (bars only) ───────── -->
          <template v-if="modelValue === 'bar'">
            <div class="my-1 border-t border-gray-200 dark:border-white/10"></div>
            <p class="px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Bar layout</p>
            <button
              type="button"
              @click="setStacked(true)"
              class="w-full px-3 py-1.5 flex items-center gap-2 text-left hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
              :class="stackedValue ? 'text-purple-600 dark:text-purple-300 font-semibold' : 'text-gray-700 dark:text-gray-200'"
            >
              <svg class="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 20h4v-6H4v6zm0-8h4V8H4v4zm6 8h4v-3h-4v3zm0-5h4V9h-4v6zm0-8h4V4h-4v3zm6 13h4v-9h-4v9zm0-11h4V7h-4v2z" /></svg>
              <span>Stacked</span>
              <svg v-if="stackedValue" class="w-4 h-4 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" /></svg>
            </button>
            <button
              type="button"
              @click="setStacked(false)"
              class="w-full px-3 py-1.5 flex items-center gap-2 text-left hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
              :class="!stackedValue ? 'text-purple-600 dark:text-purple-300 font-semibold' : 'text-gray-700 dark:text-gray-200'"
            >
              <svg class="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 20V10m4 10V4m4 16v-7m4 7V8m4 12v-5" /></svg>
              <span>Grouped (side-by-side)</span>
              <svg v-if="!stackedValue" class="w-4 h-4 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" /></svg>
            </button>
          </template>

          <!-- ───────── Fill (curve/line only) ───────── -->
          <template v-if="modelValue !== 'bar'">
            <div class="my-1 border-t border-gray-200 dark:border-white/10"></div>
            <p class="px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Fill</p>
            <button
              type="button"
              @click="setFilled(!filledValue)"
              class="w-full px-3 py-1.5 flex items-center gap-2 text-left hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
              :class="filledValue ? 'text-purple-600 dark:text-purple-300 font-semibold' : 'text-gray-700 dark:text-gray-200'"
            >
              <span class="w-4 h-4 inline-flex items-center justify-center">
                <span class="w-3.5 h-3.5 rounded border border-gray-400 dark:border-gray-500 flex items-center justify-center" :class="filledValue ? 'bg-purple-500 border-purple-500' : ''">
                  <svg v-if="filledValue" class="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" /></svg>
                </span>
              </span>
              <span>Fill area under {{ modelValue === 'curve' ? 'curves' : 'lines' }}</span>
            </button>
          </template>

          <!-- ───────── Display ───────── -->
          <div class="my-1 border-t border-gray-200 dark:border-white/10"></div>
          <p class="px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Display</p>
          <button
            type="button"
            @click="setDataLabels(!dataLabelsValue)"
            class="w-full px-3 py-1.5 flex items-center gap-2 text-left hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
            :class="dataLabelsValue ? 'text-purple-600 dark:text-purple-300 font-semibold' : 'text-gray-700 dark:text-gray-200'"
          >
            <span class="w-4 h-4 inline-flex items-center justify-center">
              <span class="w-3.5 h-3.5 rounded border border-gray-400 dark:border-gray-500 flex items-center justify-center" :class="dataLabelsValue ? 'bg-purple-500 border-purple-500' : ''">
                <svg v-if="dataLabelsValue" class="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" /></svg>
              </span>
            </span>
            <span>Show data labels</span>
          </button>

          <div class="px-3 py-2 flex items-center justify-between gap-2">
            <label class="text-sm text-gray-700 dark:text-gray-200">Series limit</label>
            <input
              type="number"
              min="1"
              max="50"
              step="1"
              :value="topNValue"
              @change="setTopN(Number(($event.target as HTMLInputElement).value))"
              @input="setTopN(Number(($event.target as HTMLInputElement).value))"
              class="glass-input w-16 text-right px-2 py-1 text-sm"
            />
          </div>
          <p class="px-3 pb-1 text-[11px] text-gray-400">
            Top {{ topNValue }} series shown; the rest bucketed as "Other".
          </p>

          <!-- ───────── Export ───────── -->
          <div class="my-1 border-t border-gray-200 dark:border-white/10"></div>
          <button
            type="button"
            @click="onExport"
            :disabled="!canExport"
            class="w-full px-3 py-2 flex items-center gap-2 text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          >
            <svg class="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V3" /></svg>
            <span>Export CSV</span>
          </button>
        </div>
      </template>
    </Teleport>
  </span>
</template>

<script setup lang="ts">
import type { ChartType } from '~/composables/useChartTypePreference'

/**
 * Reusable chart-options picker. Use with v-model for the type and
 * separate v-model:* bindings for the other settings:
 *
 *   <ChartTypeMenu
 *     v-model="chartType"
 *     v-model:stacked="stacked"
 *     v-model:filled="filled"
 *     v-model:dataLabels="dataLabels"
 *     v-model:topN="topN"
 *     :can-export="rows.length > 0"
 *     @export="onExportCsv"
 *   />
 *
 * The dropdown is teleported to <body> so it never gets clipped by a
 * panel's overflow boundary. Position is computed from the trigger
 * button's viewport rect on open and on scroll / resize.
 */
const props = defineProps<{
  modelValue: ChartType
  stacked?: boolean
  filled?: boolean
  dataLabels?: boolean
  topN?: number
  canExport?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: ChartType): void
  (e: 'update:stacked', value: boolean): void
  (e: 'update:filled', value: boolean): void
  (e: 'update:dataLabels', value: boolean): void
  (e: 'update:topN', value: number): void
  (e: 'export'): void
}>()

const open = ref(false)
const buttonEl = ref<HTMLElement | null>(null)
// Estimated dropdown width; matches the `w-64` Tailwind class on the
// menu container. Used to anchor the right edge to the button.
const MENU_WIDTH = 256
// Approximate menu height for the bottom-of-viewport check. Real
// height varies with the conditional sections (bar layout / fill);
// 360 covers the worst case (bar mode with full options).
const MENU_HEIGHT_HINT = 360
// Gap between the trigger button and the dropdown's edge.
const GAP = 4

const menuStyle = ref<{ top: string, left: string }>({ top: '0px', left: '0px' })

function position() {
  const btn = buttonEl.value
  if (!btn) return
  const rect = btn.getBoundingClientRect()
  // Default: drop below the button, right edge aligned with button.
  let top = rect.bottom + GAP
  // Flip up if there's not enough room below.
  if (top + MENU_HEIGHT_HINT > window.innerHeight && rect.top > MENU_HEIGHT_HINT + GAP) {
    top = rect.top - MENU_HEIGHT_HINT - GAP
  }
  let left = rect.right - MENU_WIDTH
  // Clamp into the viewport on narrow screens / left-aligned buttons.
  if (left < 8) left = 8
  if (left + MENU_WIDTH > window.innerWidth - 8) left = window.innerWidth - MENU_WIDTH - 8
  menuStyle.value = { top: `${top}px`, left: `${left}px` }
}

function toggle() {
  open.value = !open.value
  if (open.value) {
    // Recompute on next frame so the trigger button's rect is final
    // (handles transitions on parent layouts).
    nextTick(position)
  }
}

// Keep the dropdown anchored to the button if the user scrolls or
// resizes while it's open. We bind only while open to avoid
// background work on every chart on the page.
watch(open, (next) => {
  if (next) {
    window.addEventListener('scroll', position, true)
    window.addEventListener('resize', position)
  }
  else {
    window.removeEventListener('scroll', position, true)
    window.removeEventListener('resize', position)
  }
})
onBeforeUnmount(() => {
  window.removeEventListener('scroll', position, true)
  window.removeEventListener('resize', position)
})

const stackedValue    = computed(() => props.stacked    ?? true)
const filledValue     = computed(() => props.filled     ?? true)
const dataLabelsValue = computed(() => props.dataLabels ?? false)
const topNValue       = computed(() => props.topN       ?? 10)
const canExport       = computed(() => props.canExport  ?? true)

const chartTypes: { value: ChartType, label: string }[] = [
  { value: 'curve', label: 'Curves' },
  { value: 'line',  label: 'Lines' },
  { value: 'bar',   label: 'Bars' },
]

function setType(value: ChartType) {
  emit('update:modelValue', value)
}
function setStacked(value: boolean)    { emit('update:stacked', value) }
function setFilled(value: boolean)     { emit('update:filled', value) }
function setDataLabels(value: boolean) { emit('update:dataLabels', value) }
function setTopN(raw: number) {
  if (!Number.isFinite(raw)) return
  const clamped = Math.max(1, Math.min(50, Math.round(raw)))
  emit('update:topN', clamped)
}
function onExport() {
  if (!canExport.value) return
  emit('export')
  open.value = false
}
</script>
